import React from "react";
import styles from "./DataTable.module.css";

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleRow?: (key: string) => void;
  onToggleAll?: () => void;
  onRowClick?: (row: T) => void;
  emptyState?: React.ReactNode;
  loading?: boolean;
}

function DataTable<T>({
  columns,
  data,
  keyExtractor,
  selectable = false,
  selectedKeys = new Set(),
  onToggleRow,
  onToggleAll,
  onRowClick,
  emptyState,
  loading = false,
}: DataTableProps<T>) {
  const allSelected = data.length > 0 && selectedKeys.size === data.length;

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {selectable && (
              <th className={styles.checkCol}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  onClick={(e) => e.stopPropagation()}
                  className={styles.checkbox}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : {}}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className={styles.loadingCell}
              >
                <div className={styles.loadingRows}>
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={styles.skeleton}
                      style={{ animationDelay: `${i * 0.08}s` }}
                    />
                  ))}
                </div>
              </td>
            </tr>
          )}

          {!loading && data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className={styles.emptyCell}
              >
                {emptyState ?? (
                  <div className={styles.defaultEmpty}>
                    <span className={styles.emptyIcon}>🔍</span>
                    <span className={styles.emptyText}>No results found</span>
                  </div>
                )}
              </td>
            </tr>
          )}

          {!loading &&
            data.map((row, i) => {
              const key = keyExtractor(row);
              const isSelected = selectedKeys.has(key);

              return (
                <tr
                  key={key}
                  className={`${styles.row} ${
                    isSelected ? styles.rowSelected : ""
                  } ${onRowClick ? styles.rowClickable : ""}`}
                  onClick={() => onRowClick?.(row)}
                  style={{ animationDelay: `${i * 0.035}s` }}
                >
                  {selectable && (
                    <td
                      className={styles.checkCol}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleRow?.(key)}
                        className={styles.checkbox}
                        aria-label={`Select row ${key}`}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key}>{col.render(row, i)}</td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;